import * as React from 'react'
import { useState, useCallback, useEffect } from 'react'
import { matchPath, Redirect, Route, Switch, useParams } from 'react-router-dom'
import { History, Location } from 'history'
import { Account } from 'auth/state'
import { PageTitle } from 'uiComponents/typography/pageElements'
import { Container, ContainerBody, FullWidthContainerBody } from 'uiComponents/settingsContainer'
import { withFeatures } from 'features'
import { StatsService } from 'http/statsService'
import Permission from 'admin/permissionRequired'
import { TicketTemplatesService } from 'ticketTemplates/ticketTemplatesService'
import { parseSearch } from 'navigation'
import { ArticleService } from 'admin/articleService'
import { AddNewTopButton, ExceptionsTopButtons, ProductsTopButtons, ProductsToolbar } from 'products/topButtons'
import { ProductListWithPath } from 'products/crud/common'
import { ManagementService } from 'settings/accountSettings/contract/managementService'
import ProductPricingDetailRouter from 'products/pricing/productPricingDetailRouter'
import PricingExceptionPage from 'products/pricing/exceptions/exceptionDetailPage'
import PricingExceptionsListPage from 'products/pricing/exceptions/exceptionsListPage'
import { PricingService } from 'products/pricing/pricingService'
import ValidityExceptionsListPage from 'products/validity/exceptions/exceptionsList'
import ValidityExceptionPage from 'products/validity/exceptions/exceptionDetailPage'
import ProductValidityDetailPage from 'products/validity/articleDetailPage'
import { ArticleConfigurationService } from 'products/articleConfigurationService'
import TimeSlotsListPage from 'timeSlots/list'
import TimeSlotGroupForm from 'timeSlots/form'
import InventoryListPage from 'inventory/list'
import OptionGroupsListPage from 'products/options/groupsList'
import OptionGroupDetails from 'products/options/groupDetails'
import OptionItemDetails from 'products/options/optionItemDetails'
import { OptionsService } from 'products/options/optionsService'
import CrowdControlForm from 'inventory/crowdControl'
import InventoryRuleForm from 'inventory/ruleEditForm'
import { ChannelsService } from 'channels/channelsService'
import HomePage from 'products/home'
import ProductTable from './productTable'
import { useMessages } from 'messagesContext'
import { usePermission } from 'admin/hocs'
import WithRoutePermission from 'routes/guards/withRoutePermission'
import { BaseRouteParams } from 'hocs'
import { PricingPlansService } from './pricing/pricingPlans'
import { ProductDetailTabsProvider, useProductDetailTabs } from 'products/productDetailTabsContext'
import ProductDetailHeadingTabs from 'products/productDetailHeadingTabs'
import { NavigationBack } from 'uiComponents/navigation/navigationBack'
import InSectionTabsSlot from 'uiComponents/navigation/sectionTabsSlot'
import ProductSectionTabs from 'products/sectionTabs'
import { PRODUCTS_TABLE_HEADER_SLOT_ID } from 'products/tableHeaderSlot'
import { useTranslation } from 'translations'
import { CapacityRoutes, useCapacityAccess } from 'capacity'
import './styles.scss'

interface ProductsContainerProps {
    history: History
    accounts: Account[]
    location: Location
    articleConfigurationService: ArticleConfigurationService
    articleService: ArticleService
    pricingService: PricingService
    pricingPlansService: PricingPlansService
    channelsService: ChannelsService
    managementService: ManagementService
    ticketTemplatesService: TicketTemplatesService
    statsService: StatsService
    optionsService: OptionsService
    hasFeature: (feature: string, accountSlug: string) => boolean
}

const ProductsContainer: React.FC<ProductsContainerProps> = (props) => {
    const { location, history, pricingService, articleConfigurationService, statsService, managementService } = props
    const { t } = useTranslation()
    const { hasPermission } = usePermission()
    const { accountSlug } = useParams<BaseRouteParams>()
    const [, setHeader] = useState('Products Overview')
    const [flattenedCategories] = useState<ProductListWithPath[] | null>(null)
    const [loadingProducts, setLoadingProducts] = useState(false)
    const [showBulkButtons, setShowBulkButtons] = useState(false)
    const [bulkEditPricing, setBulkEditPricing] = useState(false)
    const [bulkEditValidity, setBulkEditValidity] = useState(false)
    const { removeAllMessages, hideMessage, replaceMessages, addMessage } = useMessages()

    const setActiveSectionAndHeader = useCallback((section: string, newHeader?: string) => {
        if (newHeader) {
            setHeader(newHeader)
        }
    }, [])

    const generateParentsPath = useCallback(
        async (
            array: { name: string; path: string; id: string }[],
            id: string,
            allLists: ProductListWithPath[] = flattenedCategories || [],
        ): Promise<{ name: string; path: string; id: string }[] | null> => {
            try {
                const parentPl = allLists.find((l) => l.uuid === id)
                const parentCategoryPath = {
                    name: parentPl?.name || t('products.index.unknownCategory'),
                    id: id,
                    path: `/account/${accountSlug}/products/home?listIds=`,
                }
                array.unshift(parentCategoryPath)

                const parentId = parentPl?.parentIds[parentPl?.parentIds.length - 2]
                if (parentId) {
                    return await generateParentsPath(array, parentId, allLists)
                } else {
                    return array
                }
            } catch {
                replaceMessages('server_error', 'error', t('products.index.unknownError'))
                return null
            }
        },
        [flattenedCategories, accountSlug, props, t],
    )

    const { detailTabs, showBackToProducts, detailTitle } = useProductDetailTabs()

    useEffect(() => {
        const linkTitle = (location.state as { title?: string } | undefined)?.title

        if (linkTitle) {
            setHeader(linkTitle)
        } else if (location.pathname.includes('/products/productTable')) {
            setHeader('Products Overview')
        }
    }, [location.pathname, location.state])

    const isCategoryDetailPage = location.pathname.includes('/products/crud/productList/')

    const params = parseSearch(location.search)
    const viewMode =
        location.pathname.indexOf('nested') > -1
            ? 'nested'
            : location.pathname.indexOf('flat') > -1
            ? 'flat'
            : ((params.from || 'nested') as 'nested' | 'flat')

    type SourceType = '?from=flat' | '?from=nested'
    const source = `?from=${params.from || viewMode}` as SourceType

    const capacityEnabled = useCapacityAccess(accountSlug)
    const capacityBareRoute =
        capacityEnabled && /\/products\/inventory(\/home|\/item\/|\/?$)/.test(location.pathname)

    const routePermissionCheckData = {
        accounts: props.accounts,
        accountSlug: accountSlug,
        history: props.history,
        hasPermission: hasPermission,
    }

    const exceptionKind = (['pricing', 'validity'] as const).find(
        (kind) =>
            location.pathname.includes(`/products/${kind}/exceptions/`) &&
            !location.pathname.includes('/exceptions/list'),
    )

    const crowdControlRule = matchPath<{ inventoryId: string }>(location.pathname, {
        path: '/account/:accountSlug/products/inventory/rule/:ruleId/item/:inventoryId',
    })
    const isCrowdControlItem = location.pathname.includes('/products/inventory/item/')
    const isTimeSlotForm =
        location.pathname.includes('/products/time_slots/') && !location.pathname.includes('/time_slots/home')
    const isOptionGroupForm =
        location.pathname.includes('/products/option_groups/') && !location.pathname.includes('/option_groups/home')
    const optionItemMatch = matchPath<{ groupUuid: string }>(location.pathname, {
        path: '/account/:accountSlug/products/option_groups/option_item/:groupUuid',
    })

    const exceptionFromProduct = typeof params.fromProduct === 'string' ? params.fromProduct : undefined

    const backLink = exceptionKind
        ? exceptionFromProduct
            ? {
                  path: `/account/${accountSlug}/products/${exceptionKind}/${exceptionFromProduct}/detail`,
                  text: `Back to product ${exceptionKind}`,
              }
            : {
                  path: `/account/${accountSlug}/products/${exceptionKind}/exceptions/list${source}`,
                  text: `Back to ${exceptionKind} exceptions`,
              }
        : crowdControlRule
        ? {
              path: `/account/${accountSlug}/products/inventory/item/${crowdControlRule.params.inventoryId}`,
              text: 'Back to crowd control item',
          }
        : isCrowdControlItem
        ? { path: `/account/${accountSlug}/products/inventory/home${source}`, text: 'Back to crowd control' }
        : isTimeSlotForm
        ? {
              path: `/account/${accountSlug}/products/time_slots/home${location.search || source}`,
              text: 'Back to time slots',
          }
        : optionItemMatch
        ? {
              path: `/account/${accountSlug}/products/option_groups/group_details/${optionItemMatch.params.groupUuid}`,
              text: 'Back to option group',
          }
        : isOptionGroupForm
        ? { path: `/account/${accountSlug}/products/option_groups/home${source}`, text: 'Back to option groups' }
        : showBackToProducts || isCategoryDetailPage
        ? { path: `/account/${accountSlug}/products/home/${viewMode}`, text: 'Back to products' }
        : null

    return (
        <div style={{ position: 'relative' }} id="pricing-settings">
            <div className="products-page-header" data-userpilot="products-page-header" />
            {!backLink && <ProductSectionTabs />}
            {detailTabs && (
                <InSectionTabsSlot>
                    {backLink ? (
                        <div className="band-back-and-tabs">
                            <NavigationBack onClick={() => history.push(backLink.path)} text={backLink.text} />
                            <ProductDetailHeadingTabs />
                        </div>
                    ) : (
                        <ProductDetailHeadingTabs />
                    )}
                </InSectionTabsSlot>
            )}
            {backLink && !detailTabs && (
                <InSectionTabsSlot>
                    <div className="detail-page-back">
                        <NavigationBack onClick={() => history.push(backLink.path)} text={backLink.text} />
                    </div>
                </InSectionTabsSlot>
            )}
            <div className="products-table-header" id={PRODUCTS_TABLE_HEADER_SLOT_ID}>
                {(location.pathname.includes('products/home') ||
                    location.pathname.includes('/products/productTable')) && (
                    <ProductsTopButtons
                        accountSlug={accountSlug}
                        origin={viewMode}
                        history={props.history}
                        location={location}
                    />
                )}
                <Permission name="edit_pricing_settings" accountSlug={accountSlug}>
                    {location.pathname.indexOf('pricing/exceptions/list') > -1 && (
                        <ExceptionsTopButtons accountSlug={accountSlug} for="pricing" />
                    )}
                </Permission>
                <Permission name="edit_product_validity" accountSlug={accountSlug}>
                    {location.pathname.indexOf('validity/exceptions/list') > -1 && (
                        <ExceptionsTopButtons accountSlug={accountSlug} for="validity" />
                    )}
                </Permission>
                <Permission name="partner_admin" accountSlug={accountSlug}>
                    {location.pathname.indexOf('option_groups/home') > -1 && (
                        <AddNewTopButton accountSlug={accountSlug} for="option_groups" />
                    )}
                    {!capacityEnabled && location.pathname.indexOf('inventory/home') > -1 && (
                        <AddNewTopButton accountSlug={accountSlug} for="inventory" />
                    )}
                </Permission>
            </div>
            {detailTabs && <PageTitle>{detailTitle || '\u00A0'}</PageTitle>}
            <Container
                style={{ marginBottom: '0' }}
                className={capacityBareRoute ? 'capacity-bare-container' : undefined}
            >
                {location.pathname.includes('products/home') && (
                    <ProductsToolbar
                        showBulkButtons={showBulkButtons}
                        loading={loadingProducts}
                        showBulkEditPricing={() => setBulkEditPricing(true)}
                        showBulkEditValidity={() => setBulkEditValidity(true)}
                    />
                )}
                <Switch location={location}>
                    <Route path="/account/:accountSlug/products/productTable" component={ProductTable} />
                    <Route
                        path="/account/:accountSlug/products/pricing/:id/detail"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_pricing_settings']}>
                                <ContainerBody>
                                    <ProductPricingDetailRouter
                                        pricingService={pricingService}
                                        articleService={props.articleService}
                                        pricingPlansService={props.pricingPlansService}
                                        channelsService={props.channelsService}
                                        articleConfigurationService={articleConfigurationService}
                                        history={history}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceTopMessages={replaceMessages}
                                        hideTopMessage={hideMessage}
                                        hasPermission={hasPermission}
                                        viewMode={viewMode}
                                    />
                                </ContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/pricing/exceptions/list"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_pricing_settings']}>
                                <FullWidthContainerBody>
                                    <PricingExceptionsListPage
                                        accountSlug={accountSlug}
                                        history={history}
                                        pricingService={pricingService}
                                        articleService={props.articleService}
                                        managementService={managementService}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceTopMessages={replaceMessages}
                                        hasPermission={hasPermission}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/pricing/exceptions/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_pricing_settings']}>
                                <ContainerBody>
                                    <PricingExceptionPage
                                        pricingService={pricingService}
                                        articleService={props.articleService}
                                        channelsService={props.channelsService}
                                        statsService={statsService}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceTopMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                        hasPermission={hasPermission}
                                    />
                                </ContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/validity/:id/detail"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_product_validity']}>
                                <ContainerBody>
                                    <ProductValidityDetailPage
                                        history={history}
                                        location={location}
                                        setActiveSection={setActiveSectionAndHeader}
                                        hasFeature={props.hasFeature}
                                        viewMode={viewMode}
                                    />
                                </ContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/validity/exceptions/list"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_product_validity']}>
                                <FullWidthContainerBody>
                                    <ValidityExceptionsListPage
                                        articleConfigurationService={articleConfigurationService}
                                        articleService={props.articleService}
                                        accountSlug={accountSlug}
                                        setActiveSection={setActiveSectionAndHeader}
                                        standalonePage
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/validity/exceptions/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_product_validity']}>
                                <ValidityExceptionPage
                                    articleConfigurationService={articleConfigurationService}
                                    articleService={props.articleService}
                                    history={history}
                                    location={location}
                                    setActiveSection={setActiveSectionAndHeader}
                                    replaceTopMessages={replaceMessages}
                                    removeAllMessages={removeAllMessages}
                                />
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/time_slots/home"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <TimeSlotsListPage
                                        history={props.history}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                        hasPermission={hasPermission}
                                        accountSlug={accountSlug}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/time_slots/edit/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <TimeSlotGroupForm
                                    history={props.history}
                                    accountSlug={accountSlug}
                                    setActiveSection={setActiveSectionAndHeader}
                                    replaceMessages={replaceMessages}
                                    removeAllMessages={removeAllMessages}
                                />
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/time_slots/duplicate/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <TimeSlotGroupForm
                                    prototype
                                    history={props.history}
                                    accountSlug={accountSlug}
                                    setActiveSection={setActiveSectionAndHeader}
                                    replaceMessages={replaceMessages}
                                    removeAllMessages={removeAllMessages}
                                />
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/option_groups/home"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <OptionGroupsListPage
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceMessages={replaceMessages}
                                        optionsService={props.optionsService}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/option_groups/group_details/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <OptionGroupDetails
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                        optionsService={props.optionsService}
                                        articleService={props.articleService}
                                        setActiveSection={setActiveSectionAndHeader}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/option_groups/option_item/:groupUuid/:itemId"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <OptionItemDetails
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                        optionsService={props.optionsService}
                                        articleService={props.articleService}
                                        setActiveSection={setActiveSectionAndHeader}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    {capacityEnabled && (
                        <Route
                            path="/account/:accountSlug/products/inventory"
                            render={() => (
                                <CapacityRoutes
                                    history={props.history}
                                    accountSlug={accountSlug}
                                    setActiveSection={setActiveSectionAndHeader}
                                    replaceMessages={replaceMessages}
                                    hideMessage={hideMessage}
                                    removeAllMessages={removeAllMessages}
                                    source={source}
                                />
                            )}
                        />
                    )}
                    <Route
                        path="/account/:accountSlug/products/inventory/home"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <InventoryListPage
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceMessages={replaceMessages}
                                        source={source}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/inventory/item/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <CrowdControlForm
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/inventory/rule/:ruleId/item/:inventoryId"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <InventoryRuleForm
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path={['/account/:accountSlug/products/home', '/account/:accountSlug/products/crud']}
                        render={() => (
                            <HomePage
                                location={location}
                                accountSlug={accountSlug}
                                pricingService={pricingService}
                                articleService={props.articleService}
                                optionsService={props.optionsService}
                                history={history}
                                setActiveSection={setActiveSectionAndHeader}
                                replaceTopMessages={replaceMessages}
                                hasPermission={hasPermission}
                                hasFeature={props.hasFeature}
                                addMessage={addMessage}
                                removeAllMessages={removeAllMessages}
                                hideMessage={hideMessage}
                                routePermissionCheckData={routePermissionCheckData}
                                ticketTemplatesService={props.ticketTemplatesService}
                                viewMode={viewMode}
                                setShowBulkButtons={setShowBulkButtons}
                                bulkEditPricing={bulkEditPricing}
                                bulkEditValidity={bulkEditValidity}
                                cancelBulkEditPricing={() => setBulkEditPricing(false)}
                                cancelBulkEditValidity={() => setBulkEditValidity(false)}
                                loading={loadingProducts}
                                setLoading={setLoadingProducts}
                            />
                        )}
                    />
                    <Route render={() => <Redirect to={`/account/${accountSlug}/products/home/nested`} />} />
                </Switch>
            </Container>
        </div>
    )
}

const ProductsContainerWithDetailTabs: React.FC<ProductsContainerProps> = (props) => (
    <ProductDetailTabsProvider>
        <ProductsContainer {...props} />
    </ProductDetailTabsProvider>
)

export default withFeatures(ProductsContainerWithDetailTabs)
